import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment';
import { API_SOURCES } from '@/config/apiSources.js';

import epochToIso from '@/util/epochToIso.js';
import { fetchSqlRows, fetchDatabridgeGeoJSON, aisAxios } from '@/util/databridge.js';

// databridge has no select *: shape must be transformed to 4326 explicitly, so columns
// are listed (the set atlas uses for the same table)
const FOOTPRINTS_DATABRIDGE_COLS = 'bin, address, building_name, approx_hgt, max_hgt, base_elevation, square_ft, parcel_id_num, parcel_id_source, fcode, objectid';

import contractorLookup from './contractorLookup';
import contractorPermitLookup from './contractorPermitLookup';
import appealsCalendar from './appealsCalendar';
import permitApplicationTracker from './permitApplicationTracker';

Vue.use(Vuex);

const esriAxios = axios.create({
  baseURL: 'https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/LI_BUILDING_FOOTPRINTS/FeatureServer/0/query'
});


//  APIs for data sources
// each builder returns a table-style spec for fetchSqlRows: the old HANSEN-arm UNION
// ECLIPSE-arm on the same table is identical to (arm) OR (arm) - each old arm wrapped
// verbatim in parens to preserve its AND/OR precedence (set-equality proven by EXCEPT
// in both directions), the ORDER BY becomes a client-side sort, and cartoSql keeps the
// old uncapped statement as the runtime fallback
const propertyHistoryArms = function(table, opaQuery, streetaddress, addressId, eclipse_location_id, pwd_parcel_id) {
  const hansenArm = `( address = '${ streetaddress }' \
          OR addressobjectid IN ('${ addressId }') \
          OR parcel_id_num IN ( '${ pwd_parcel_id }' ) ) \
          ${ opaQuery } \
          AND systemofrecord IN ('HANSEN')`;
  const eclipseArm = `( addressobjectid IN ('${ eclipse_location_id }') \
          OR parcel_id_num IN ( '${ pwd_parcel_id }' ) ) \
          ${ opaQuery } \
          AND systemofrecord IN ('ECLIPSE')`;
  return {
    where: `(${hansenArm}) OR (${eclipseArm})`,
    unionSql: `SELECT * FROM ${table} WHERE ${hansenArm} UNION SELECT * FROM ${table} WHERE ${eclipseArm}`,
  };
}
const violationQuery = function(opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id) {
  let opaQuery = opa_account_num ? ` OR opa_account_num IN ('${ opa_account_num}')` : ``
  const arms = propertyHistoryArms('VIOLATIONS', opaQuery, streetaddress, addressId, eclipse_location_id, pwd_parcel_id);
  return {
    table: 'violations',
    where: arms.where,
    orderBy: { column: 'casenumber', descending: true },
    cartoSql: `${arms.unionSql} ORDER BY casenumber DESC`,
  };
}
const permitsQuery = function(opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id){
  let opaQuery = opa_account_num ? ` OR opa_account_num IN ('${ opa_account_num }')` : ``
  const arms = propertyHistoryArms('PERMITS', opaQuery, streetaddress, addressId, eclipse_location_id, pwd_parcel_id);
  return {
    table: 'permits',
    where: arms.where,
    orderBy: { column: 'permittype', descending: false },
    cartoSql: `${arms.unionSql} ORDER BY permittype`,
  };
}

const liInvestigationQuery = function(opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id) {
  let opaQuery = opa_account_num ? ` OR opa_account_num IN ('${ opa_account_num }')` : ``
  const arms = propertyHistoryArms('case_investigations', opaQuery, streetaddress, addressId, eclipse_location_id, pwd_parcel_id);
  return {
    table: 'case_investigations',
    where: arms.where,
    cartoSql: arms.unionSql,
  };
}

const appealsQuery = function(opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id) {
  let opaQuery = opa_account_num ? ` AND opa_account_num IN ('${ opa_account_num }')` : ``
  const arms = propertyHistoryArms('APPEALS', opaQuery, streetaddress, addressId, eclipse_location_id, pwd_parcel_id);
  return {
    table: 'appeals',
    where: arms.where,
    orderBy: { column: 'scheduleddate', descending: true },
    cartoSql: `${arms.unionSql} ORDER BY scheduleddate DESC`,
  };
}

// Does not contain hansen/eclipse field
const licensesQuery = function(opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id) {
  // console.log(opa_account_num, streetaddress, addressId, eclipse_location_id)
  streetaddress
  addressId
  let value;
  let opaQuery = opa_account_num ? ` OR opa_account_num IN ('${ opa_account_num}')` : ``
  
  if (eclipse_location_id) {
    value = `( addressobjectid IN ('`+ eclipse_location_id +`') AND addressed_license = 'Yes' \
    OR address = '${streetaddress}' AND addressed_license = 'Yes' \
    OR parcel_id_num IN ( '${ pwd_parcel_id }' ) AND addressed_license = 'Yes' ) \
    ${opaQuery }`;
  } else {
    value = `( address = '${streetaddress}' AND addressed_license = 'Yes' \
    OR parcel_id_num IN ( '${ pwd_parcel_id }' ) AND addressed_license = 'Yes' ) \
    ${opaQuery }`;
  }
  return {
    table: 'business_licenses',
    where: value,
    orderBy: { column: 'licensetype', descending: false },
    cartoSql: `SELECT * FROM BUSINESS_LICENSES WHERE ${value} ORDER BY licensetype`,
  };
}

const appealsCourtHistoryQuery = function(appeal) {
  return `SELECT * FROM COURT_APPEALS WHERE appealnumber = ('${appeal}') ORDER BY courtactiondate DESC`;
}
const appealsZoningBoardQuery = function(appeal) {
  return `SELECT * FROM Board_Decisions WHERE appealnumber = ('${appeal}')`;
}
const mailingAddressQuery = function(pwd_parcel_id) {
  // the key column is pwd_parcel_id, matching the arcgis branch - the old address_std
  // here never matched anything, which the arcgis flag hid until databridge ran it.
  // objectid rides along so the walk's cursor works if a parcel ever spans 999+ units
  return {
    table: 'opa_properties_public_pde',
    fields: 'mailing_address_1, mailing_address_2, mailing_street, mailing_care_of, mailing_city_state, mailing_zip, objectid',
    where: `pwd_parcel_id = ('${pwd_parcel_id}')`,
  };
}
const buildingCertificationSummaryQuery = function(value) {
  let binValues = value.replace(/\|/g, "', '");
  return { table: 'building_cert_summary', where: `structure_id IN ('${binValues}')` };
}
const buildingCertsQuery = function(value) {
  let binValues = value.replace(/\|/g, "', '");
  return { table: 'building_certs', where: `bin IN ('${binValues}')` };
}

const zoningDocQuery = function(permitNumber) {
  return { table: 'ais_zoning_documents', where: `permit_number = ('${permitNumber}')` };
}
const zoningDocQueryLI = function(permitNumber) {
  return { table: 'li_zoning_docs', where: `permit_number = ('${permitNumber}')` };
}

const sizeOf = async function(value, eclipse) {
  // console.log(value, eclipse);
  if (eclipse) {
    let size = await axios.head('https://s3.amazonaws.com/eclipse-docs-pdfs/zoning/' + value)
                .then(function(response){
                  // console.log("head response: ", response)
                  let size = (response.headers["content-length"]*0.000001).toFixed(2);
                  return size
                })
                .catch(error => console.log( error))
    return size
  } else {
    let size = await axios.head('https://s3.amazonaws.com/lni-zoning-pdfs/' + value)
                .then(function(response){
                  let size = (response.headers["content-length"]*0.000001).toFixed(2);
                  return size
                })
                .catch(error => console.log( error))
    return size
  }
}

// const addressObjectIdCompiled = function(feature) {
//   var eclipseLocId = feature.properties.eclipse_location_id.split('|');
//   var li_address_key = feature.properties.li_address_key.split('|');
//   var j;
//   var str = "'";
//   for (j = 0; j < li_address_key.length; j++) {
//     str += li_address_key[j];
//     str += "', '";
//   }
//   var i;
//   for (i = 0; i < eclipseLocId.length; i++) {
//     str += eclipseLocId[i];
//     str += "', '";
//   }
//   str = str.slice(0, str.length - 3);
//   console.log("str: ", str);

//   return str
// }



//Mutation helpers to format API into the State for display and navigation through components

const violationTableData = function(violationGroup) {
  let violationArray = []
  Object.keys(violationGroup).forEach(
    a => violationArray.push( violationGroup[a] )
  )
  return violationArray
}

// this function seems to set up the violation data for the violation table
// by grouping the violations by case number and then creating a new object.
// Unfortunately, all of the data it sets up does not make it to the state
// on 2/22/24 it was noticed that the violations table was not displaying the correct data.
// This bug was fixed in the views/PropertyHistory/SearchResults.vue template
// without making any changes to this function.
// As a result, this function seems is running but not doing anything.
const violationResults = function(state, payload) {
  // console.log("violationResults payload: ", payload.length)
  // let violationMsgArray = [];

  let violationGroup = _.groupBy(payload, 'casenumber')
  // console.log(violationGroup)
  Object.keys(violationGroup).forEach(
    function(a) {
      let titleDesc = violationGroup[a][0].caseprioritydesc === null ? "No Description" : violationGroup[a][0].caseprioritydesc
      violationGroup[a].text = violationGroup[a][0].casenumber+ " " + titleDesc
      violationGroup[a].href = 'violation-detail';
      violationGroup[a].violationType = violationGroup[a][0].caseprioritydesc ? violationGroup[a][0].caseprioritydesc : 'Not Available';
      violationGroup[a].violationStatus = violationGroup[a][0].casestatus;
      violationGroup[a].violationNumber = violationGroup[a][0].casenumber;
      violationGroup[a].opa_account_num = violationGroup[a][0].opa_account_num;

      violationGroup[a].violationAddress = [];
      violationGroup[a].violationAddress.push(violationGroup[a][0].address);
      violationGroup[a][0].unit_num !== null ? violationGroup[a].violationAddress.push([violationGroup[a][0].unit_type !== null ? a.unit_type : "Unit", violationGroup[a][0].unit_num].join(" ")):"";
      violationGroup[a].violationAddress.push("Philadelphia, PA " + violationGroup[a][0].zip)

      violationGroup[a].violationZip = violationGroup[a][0].zip;
      violationGroup[a].violationOwner = violationGroup[a][0].opa_owner;
      violationGroup[a].violationMailingAddress = [];
      violationGroup[a].violationMailingAddress.push(violationGroup[a][0].address);
      violationGroup[a].violationAddress2 ? violationGroup[a].violationMailingAddress.push(violationGroup[a].violationAddress2): "";
      violationGroup[a].violationMailingAddress.push("Philadelphia, PA " + violationGroup[a][0].zip);
      violationGroup[a].violationPriority = violationGroup[a][0].caseprioritydesc ? violationGroup[a][0].caseprioritydesc : "Not Available";
      violationGroup[a].indexGroup= moment(violationGroup[a][0].casecreateddate).format('yyyy');
      violationGroup[a].violationDisplayDate = violationGroup[a][0].casecreateddate;
      violationGroup[a].violationCreatedDate = moment(violationGroup[a][0].casecreateddate).isValid() ? moment(violationGroup[a][0].casecreateddate).format('MMM DD, yyyy') : "Not Available";
      violationGroup[a].violationUpdatedDate = moment(violationGroup[a][0].mostrecentinvestigation).isValid() ? moment(violationGroup[a][0].mostrecentinvestigation).format('MMM DD, yyyy') : "Not Available";
      violationGroup[a].violationResolutionDate = moment(violationGroup[a][0].casecompleteddate).isValid() ? moment(violationGroup[a][0].casecompleteddate).format('MMM DD, yyyy') : "Not Available";
      violationGroup[a].publicnov = violationGroup[a][0].publicnov;
      return violationGroup;
    }
  )
  // console.log(violationGroup);
  return violationGroup
}
const investigationsResults = function(state, payload){
  let investigationsMsgArray = [];
  payload.map(
    a => {
      a.indexGroup = moment(a.investigationcompleted).format('yyyy');
      a.href = 'investigation-detail';
      investigationsMsgArray.push(a)
      if (a.investigationstatus == null) {
        a.investigationstatus = 'Not Available';
      }
    }
  )
  return investigationsMsgArray
}

const appealsResults = function(state, payload) {

  // IN WORK --- Change everything below to match appeals detail
  // console.log("permitResults payload: ", payload)
  let appealMsgArray = [];
  payload.map(
    a => {
      let appealRecord = new Object();
      let appealType = a.applicationtype === "RB_ZBA" ? " Zoning" : " Other";
      appealRecord.text = a.appealnumber + " " + appealType;
      appealRecord.href = 'appeal-detail';
      appealRecord.appealsNumber = a.appealnumber;
      appealRecord.indexGroup = '';

      appealRecord.appealsAddress = [];
      appealRecord.appealsAddress.push(a.address);
      a.unit_num !== null ? appealRecord.appealAddress.push([a.unit_type !== null ? a.unit_type : "Unit", a.unit_num].join(" ")): "";
      appealRecord.appealsAddress.push("Philadelphia, PA " + a.zip);

      appealRecord.appealsStatus = a.appealstatus;
      appealRecord.appealsOwner = a.opa_owner;
      appealRecord.appealsMailingAddress = ["Need Mailing Address", "Need Mailing Address 2nd Line"];
      appealRecord.appealsType = appealType;
      appealRecord.appealsGrounds = a.appealgrounds;
      appealRecord.appealsPrimaryAppellant = a.primaryappellant;
      appealRecord.appealsPermitNumber = "N/A";
      appealRecord.opa_account_num = a.opa_account_num;
      appealRecord.scheduleddate = a.scheduleddate;
      appealRecord.applicationtype = a.applicationtype;
      appealRecord.coordinatingrco = a.coordinatingrco;

      appealMsgArray.push(appealRecord);
    }
  )
  return appealMsgArray
}

const buildingCertResults = function(state, payload) {
  // console.log("buildingCertResults payload: ", payload);
  let certMsgArray = [];
  payload.map(
    a => {
      let buildingCert = new Object();
      buildingCert.structure_id = a.structure_id;
      buildingCert.href = 'building-certification-detail';
      buildingCert.total = Object.values(a).filter(a => a !== null).length;
      let plural = buildingCert.total === 1 ? " certification" : " certifications";
      buildingCert.total === 0 ? state.appealsResults.results = "No certifications for this property" :
      buildingCert.totalMsg = buildingCert.total + plural;   
      certMsgArray.push(buildingCert);
    }
  )
  return certMsgArray
}

const buildingCertificationSummaryResults = function(state, payload) {
  // console.log("buildingCertificationSummaryResults payload: ", payload, 'state:', state, 'state.buildingCerts[0]:', state.buildingCerts[0]);
  let certMsgArray = [];
  payload.map(
    a => {
      let structure_id = a.structure_id;
      let buildingCerts = state.buildingCerts.filter(b => b.bin === structure_id);
      // console.log('index buildingCertSummary payloadmap, a:', a, 'buildingCerts:', buildingCerts);
      let buildingCert = new Object();
      buildingCert.structure_id = a.structure_id;
      buildingCert.href = 'building-certification-detail';
      buildingCert.total = buildingCerts.length;
      let plural = buildingCert.total === 1 ? " certification" : " certifications";
      buildingCert.total === 0 ? state.appealsResults.results = "No certifications for this property" :
      buildingCert.totalMsg = buildingCert.total + plural;   
      certMsgArray.push(buildingCert);
    }
  )
  return certMsgArray
}

const licenseResults = function(state, payload) {
  // console.log("permitResults payload: ", payload)
  let licenseMsgArray = [];
  payload.map(
    a => {
      let licenseRecord = new Object();
      licenseRecord.text = a.licensenum + " " + a.licensetype;
      licenseRecord.href = 'business-license-detail';
      licenseRecord.licenseNumber = a.licensenum;
 
      licenseRecord.licenseAddress = [];
      licenseRecord.licenseAddress.push(a.address);
      a.unit_num !== null ? licenseRecord.licenseAddress.push([a.unit_type !== null ? a.unit_type : "Unit", a.unit_num].join(" ")): "";
      licenseRecord.licenseAddress.push("Philadelphia, PA " + a.zip);
      licenseRecord.BusinessMailingAddress = a.business_mailing_address ? a.business_mailing_address.toUpperCase().split("PHILADELPHIA,") : "";
      licenseRecord.BusinessMailingAddress[1] ? 
        licenseRecord.BusinessMailingAddress[1] = "Philadelphia," + licenseRecord.BusinessMailingAddress[1] : "";
      licenseRecord.BusinessName = a.business_name;

      licenseRecord.licenseStatus = a.licensestatus;
      licenseRecord.licenseOwner = a.opa_owner;
      licenseRecord.licenseType = [a.revenuecode, a.licensetype].join(" ");
      licenseRecord.rentalCategory = a.rentalcategory
      licenseRecord.indexGroup = moment(a.initialissuedate).format('yyyy');
      licenseRecord.licenseDateIssued = a.initialissuedate;
      licenseRecord.licenseDateInactive = a.inactivedate;
      licenseRecord.licenseContact= a.legalname
      licenseRecord.licenseContactAddress = [...licenseRecord.BusinessMailingAddress]
      licenseRecord.licenseContactType = a.legalentitytype;
      licenseRecord.opa_account_num = a.opa_account_num;
      licenseRecord.licenseUnits = a.numberofunits;
      licenseRecord.addressedLicense = a.addressed_license;

      licenseRecord.ownercontact1 = [];
      licenseRecord.ownercontact1.push(a.ownercontact1name);
      licenseRecord.ownercontact1.push(a.ownercontact1mailingaddress);
      licenseRecord.ownercontact1.push(a.ownercontact1city + ", " + a.ownercontact1state + " " + a.ownercontact1zippostalcode);

      
      licenseRecord.ownercontact2 = [];
      licenseRecord.ownercontact2.push(a.ownercontact2name);
      licenseRecord.ownercontact2.push(a.ownercontact2mailingaddress);
      licenseRecord.ownercontact2.push(a.ownercontact2city + ", " + a.ownercontact2state + " " + a.ownercontact2zippostalcode);

      licenseMsgArray.push(licenseRecord);
    }
  )
  return licenseMsgArray
}

const permitResults = function(state, payload) {
  // console.log("permitResults payload: ", payload)
  let permitMsgArray = [];
  payload.map(
    a => {
      let permitRecord = new Object();
      permitRecord.text = a.permitnumber + " " + a.permitdescription;
      permitRecord.href = 'permit-detail';
      permitRecord.permitNumber = a.permitnumber;

      permitRecord.permitAddress = [];
      permitRecord.permitAddress.push(a.address);
      a.unit_num !== null ? permitRecord.permitAddress.push([a.unit_type !== null ? a.unit_type : "Unit", a.unit_num].join(" ")): "";
      permitRecord.permitAddress.push("Philadelphia, PA " + a.zip);

      permitRecord.permitDate = a.permitissuedate ;
      permitRecord.permitLIDistrict = state.searchResults.li_district;
      permitRecord.permitOwnerName = a.opa_owner;
      permitRecord.permitOwnerMailingAddress = ["Need Mailing Address", "Need Mailing Address 2nd Line"];
      permitRecord.permitType = a.permittype;
      permitRecord.permitTypeOfWork = a.typeofwork;
      permitRecord.permitStatus = a.status;
      permitRecord.indexGroup = moment(a.permitissuedate).format('yyyy');
      permitRecord.permitIssuedDate = a.permitissuedate;
      permitRecord.permitScopeOfWork = a.approvedscopeofwork;
      permitRecord.permitDescription = a.permitdescription;
      permitRecord.opa_account_num = a.opa_account_num;

      permitRecord.permitContractorname = a.contractorname ? a.contractorname : "";
      permitRecord.permitContractorAddress = []
      a.contractoraddress1 ? permitRecord.permitContractorAddress.push(a.contractoraddress1) : permitRecord.permitContractorAddress.push("");
      a.contractoraddress2 !== null ? permitRecord.permitContractorAddress.push(a.contractoraddress2) : "" ;
      permitRecord.permitContractorAddress.push([ a.contractorcity, a.contractorstate, a.contractorzip ].join(" "));

      // if(a.subcontractorname){
      //   permitRecord.permitSubContractorname = a.subcontractorname;
      //   permitRecord.permitSubContractorAddress = []     
      //   permitRecord.permitContractorAddress.push(a.subcontractoraddress1) 
      //   a.subcontractoraddress2 !== null ? permitRecord.permitContractorAddress.push(a.subcontractoraddress2) : "" ;
      //   permitRecord.permitContractorAddress.push([ a.subcontractorcity, a.subcontractorstate, a.subcontractorzip ].join(" "));
      // }
      permitMsgArray.push(permitRecord);
    }
  )
  return permitMsgArray
}

let localStore = {
// export default new Vuex.Store({
  modules: {
    contractorLookup,
    contractorPermitLookup,
    appealsCalendar,
    permitApplicationTracker,
  },
  state: {
    appealsCourtHistory: null,
    propertyHistorySearchStatus: false,
    activeDetail: {},
    detailPage: false,
    violationCodeInfo: null,
    headerProps: null,
    appealsResults: {
      title: 'Appeals',
      results: 'No Appeals',
      msg: [],
    },
    buildingCertificationSummaryResults: {
      title: 'Building Certifications',
      results: 'No Certifications',
      msg: [],
    },
    buildingCertResults: {
      title: 'Building Certs',
      results: 'No Certifications',
      msg: [],
    },
    investigationsResults: {
      title: 'Investigations',
      results: 'No investigations',
      msg: [],
    },
    permitResults: {
      title: 'Permits',
      results: 'No permits',
      msg: [],
    },
    licenseResults: {
      title: 'Business licenses',
      results: 'No business licenses',
      msg: [],
    },
    violationResults: {
      title: 'Violations',
      results: 'No violations',
      msg: [],
    },
    violationsTableResults: {
      title: 'Violations',
      results: 'No violations',
      msg: [],
    },
    map: {
      center: [ -75.163471, 39.953338 ],
      zoom: 18,
      map: null,
      basemap: 'pwd',
      imagery: 'imagery2023',
      imageryOn: false,
    }
  },
  mutations: {
    updateHeader(state, value) {
      // console.log("updateHeader: ", value)
      return Vue.set(state,'headerProps', value);
    },
    openDetail(state, value) {
      console.log('openDetail, value', value);
      return Vue.set(state,'detailPage', value);
    },
    closeDetail(state) {
      // console.log("closing detail page")
      return Vue.set(state, 'detailPage', false);
    },
    openCodeDetail(state, value) {
      return state.violationCodeInfo = value;
    },
    closeCodeDetail(state) {
      state.violationCodeInfo = null;
    },
    setActiveDetail(state, payload) {
      return state.activeDetail = payload;
    },
    propHistStatus(state){
      return state.propertyHistorySearchStatus = true;
    },
    setAddressData(state, data) {
      // console.log("setting address data");
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      Vue.set(state, 'searchResults', data[0]);
      // console.log(state);
    },
    setViolationsData(state, data) {
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      let resultsObject = violationResults(state, data[0].data.rows);
      if(data[0].data) {
        state.violationsTableResults.msg = violationTableData(resultsObject);
        let total = data[0].data.rows.length
        let plural = _.size(resultsObject) === 1 ? " Violation" : " cases";
        _.size(resultsObject) === 0 ? state.violationsTableResults.results = "No Violations for this property" :
          total === 1 ? state.violationsTableResults.results = _.size(resultsObject) + plural :
          state.violationsTableResults.results = total + " Violations" + " (within " + _.size(resultsObject) + plural + ")";
      } else {
        state.violationsTableResults = data;
      }
      if(data[0].data) {
        state.violationResults.msg = violationResults(state, data[0].data.rows);
        let plural = _.size(state.violationResults.msg) === 1 ? " Violation" : " Violations";
        _.size(state.violationResults.msg) === 0 ? state.violationResults.results = "No Violations for this property" :
          state.violationResults.results = _.size(state.violationResults.msg) + plural;
      } else {
        state.violationsResults = data;
      }
    },
    setAppealsData(state, data) {
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      if(data[0].data) {
        state.appealsResults.msg = appealsResults(state, data[0].data.rows);

        let plural = state.appealsResults.msg.length === 1 ? " Appeal" : " Appeals";
        state.appealsResults.msg.length === 0 ? state.appealsResults.results = "No Appeals for this property" :
          state.appealsResults.results = state.appealsResults.msg.length + plural;
      } else {
        state.appealsResults = data;
      }
    },

    setInvestigationData(state, data){
      // console.log("setInvestigationsData: ", data);
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      // console.log("setInvestigationsData: ", data[0].data);
      if(data[0].data) {
        state.investigationsResults.msg = investigationsResults(state, data[0].data.rows);
        // console.log("setInvestigationsData: ", data[0].data.rows);

        let plural = state.investigationsResults.msg.length === 1 ? " Investigation" : " Investigations";
        state.investigationsResults.msg.length === 0 ? state.investigationsResults.results = "No Investigations for this property" :
          state.investigationsResults.results = state.investigationsResults.msg.length + plural;
      } else {
        state.investigationsResults = data;
      }
    },
    setLicensesData(state, data) {
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      if(data[0].data) {
        state.licenseResults.msg = licenseResults(state, data[0].data.rows);

        let plural = state.licenseResults.msg.length === 1 ? " Business license" : " Business licenses";
        state.licenseResults.msg.length === 0 ? state.licenseResults.results = "No business licenses for this property" :
          state.licenseResults.results = state.licenseResults.msg.length + plural;
      } else {
        state.licensesResults = data;
      }
    },
    setPermitsData(state, data) {
      // console.log("Permits Data Mutation: ", state, data);
      // A test
      if (!Array.isArray(data)) {
        data = [ data ];
      }
      if(data[0].data) {
        // console.log("TODO Fix .length() function for results totals field", state, state.permitResults);
        state.permitResults.msg = permitResults(state, data[0].data.rows);
        let plural = state.permitResults.msg.length === 1 ? " Permit" : " Permits";
        state.permitResults.msg.length === 0 ? state.permitResults.results = "No Permits for this property" :
          state.permitResults.results = state.permitResults.msg.length + plural + " for this property";
      } else {
        Vue.set(state, 'permitResults', data)
      }
    },
    setBuildingCertificationSummary(state, data) {
      // console.log("Building Certifications Summary Data Mutation, state:", state, 'state.buildingCerts:', state.buildingCerts, 'data:', data);
      state.buildingCertificationSummaryResults.msg = buildingCertificationSummaryResults(state, data.data.rows);
      let total = 0;
      state.buildingCertificationSummaryResults.msg.map(a => total += a.total);
      state.buildingCertificationSummaryResults.total = total;
      let plural = state.buildingCertificationSummaryResults.total === 1 ? " Certification" : " Certifications";
      state.buildingCertificationSummaryResults.total === 0 ? state.buildingCertificationSummaryResults.results = "No certifications for this property" :
        state.buildingCertificationSummaryResults.results = state.buildingCertificationSummaryResults.total + plural;

      // console.log("Court data: ", data);
      if(data.data.rows.length > 0 ) {
        return Vue.set(state, 'buildingCertificationSummary', data.data.rows);
      }
      return data;
    },
    setBuildingCerts(state, data) {
      // console.log("Building Certs Data Mutation: ", data);
      state.buildingCertResults.msg = buildingCertResults(state, data.data.rows);
      let total = 0;
      state.buildingCertResults.msg.map(a => total += a.total);
      state.buildingCertResults.total = total;
      let plural = state.buildingCertResults.total === 1 ? " Certification" : " Certifications";
      state.buildingCertResults.total === 0 ? state.buildingCertResults.results = "No certifications for this property" :
        state.buildingCertResults.results = state.buildingCertResults.total + plural;

      // console.log("Court data: ", data);
      if(data.data.rows.length > 0 ) {
        return Vue.set(state, 'buildingCerts', data.data.rows);
      }
      return data;
    },
    setBuildingFootprints(state, data) {
      // console.log('setBuildingFootprints, data:', data);
      if(data.data.features.length > 0 ) {
        return Vue.set(state, 'buildingFootprints', data.data.features);
      }
      return data;
    },
    setMailingAddress(state, data) {
      // console.log("Court data: ", data);
      if(data.data.rows.length > 0 ) {
        return Vue.set(state, 'mailingAddress', data.data.rows[0]);
      }
      return data;
    },
    setAppealsCourtHistory(state, data) {
      // console.log("Court data: ", data);
      if(data.data.rows.length > 0 ) {
        return Vue.set(state, 'appealsCourtHistory', data.data.rows);
      }
      return state.appealsCourtHistory = null
    },
    setZoningBoardHistory(state, data) {
      // console.log("Court data: ", data);
      if(data.data.rows.length > 0 ) {
        return Vue.set(state, 'zoningBoardHistory', data.data.rows);
      }
      return state.zoningBoardHistory = null
    },
    setViolationDetail(state, data) {
      return state.violationDetail = data;
    },
    setZoningDocs(state, data) {
      // console.log("setZoningDocs: ", data)
      return state.zoningDocs = data;
    },
    setZoningDocFileSizes(state, sizeArray) {
      // console.log("sizeArray", sizeArray);
      return Vue.set(state, 'zoningDocsFileSizes', sizeArray);
    },
    setImagery(state, payload) {
      state.map.imagery = payload;
    },
    setBasemap(state, payload) {
      state.map.basemap = payload;
    },
    setMapCenter(state, payload) {
      state.map.center = payload;
    },
    setImageryOn(state, payload) {
      state.map.imageryOn = payload;
    },
  },
  getters: {
    detailState: (state, value) => {
      return state[value]
    }
  },
  actions: {
    async getZoningDocInfoLI( { commit }, permitNumber) {
      // console.log("Get zoning doc info");
      commit;
      let addDocArray = [];
      try {
        await fetchSqlRows('zoningDocs', zoningDocQueryLI(permitNumber)).then(
          function(response) {
            // console.log("LI zoning doc response", response);
            if(response.data && response.data.rows) {
              // console.log("LI zoning doc response", response.data.rows);
              response.data.rows.forEach( function(a) {
                // console.log(a)
                let zoningDocObj = { 
                  address: a.permit_location ? a.permit_location.split(",")[0] : "",
                  app_doc_id: null,
                  app_id: "",
                  cartodb_id: null,
                  doc_id: a.permit_number.toString(),
                  num_pages: a.pages_scanned ? a.pages_scanned : "N/A",
                  permit_number: a.permit_number,
                  scan_date: a.issue_date,
                  the_geom: a.the_geom,
                  the_geom_webmercator: a.the_geom_webmercator,
                  eclipse: true,
                };
                addDocArray.push( zoningDocObj )
                return addDocArray
              });
              // console.log(addDocArray)
              return addDocArray
            }
          });
        } catch (err) {
          console.warn("Court History API error: ", err)
          // return false;
        }
        // console.log(addDocArray)
        return addDocArray
      // return addDocArray
    },
    async getZoningDocInfo( { dispatch, commit }, permitNumber) {
      // console.log("Get zoning doc info");
      try {
        let zoningDocArray = [];
        await fetchSqlRows('zoningDocs', zoningDocQuery(permitNumber)).then(
          async function(response) {
            // console.log(response);
            if(response.data && response.data.rows) {
              zoningDocArray = response.data.rows
              let additionalZoningDocs = await dispatch('getZoningDocInfoLI', permitNumber);
              zoningDocArray = zoningDocArray.concat(additionalZoningDocs)
              commit('setZoningDocs', zoningDocArray) ;
              return zoningDocArray;
            }
          });
        return zoningDocArray;
      } catch (err) {
        console.warn("Court History API error: ", err)
        return false;
      } 
    },
    async getMailingAddress( { commit }, pwd_parcel_id) {
      try {
        let mailingAddress;
        if (API_SOURCES.mailingAddress === 'arcgis') {
          const whereClause = `pwd_parcel_id='${pwd_parcel_id}'`;
          const outFields = 'mailing_address_1,mailing_address_2,mailing_street,mailing_care_of,mailing_city_state,mailing_zip';
          const url = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/opa_properties_public_pde/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=${outFields}&f=json`;
          const response = await fetch(url);
          if (response.ok) {
            const jsonData = await response.json();
            mailingAddress = {
              data: {
                rows: jsonData.features ? jsonData.features.map(f => f.attributes) : []
              }
            };
          } else {
            console.warn("MailingAddress API error: response not ok");
            return false;
          }
        } else {
          mailingAddress = await fetchSqlRows('mailingAddress', mailingAddressQuery(pwd_parcel_id));
        }
        commit('setMailingAddress', mailingAddress);
        return true;
      } catch (err) {
        console.warn("MailingAddress API error: ", err)
        return false;
      }
    },
    async getBuildingCertificationSummary( { commit }, bin) {
      // console.log("getBuildingCertificationSummary: ", bin)
      try {
        let buildingCertificationSummary;
        if (API_SOURCES.buildingCertSummary === 'arcgis') {
          let binValues = bin.split('|');
          let whereClause = binValues.map(b => `STRUCTURE_ID='${b}'`).join(' OR ');
          const url = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/BUILDING_CERT_SUMMARY/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&f=json`;
          const response = await fetch(url);
          if (response.ok) {
            const jsonData = await response.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              return attrs;
            }) : [];
            buildingCertificationSummary = { data: { rows: mappedRows } };
          } else {
            buildingCertificationSummary = { data: { rows: [] } };
          }
        } else {
          buildingCertificationSummary = await fetchSqlRows('buildingCertSummary', buildingCertificationSummaryQuery(bin));
        }
        // console.log("setting buildingCertificationSummary:", buildingCertificationSummary);
        commit('setBuildingCertificationSummary', buildingCertificationSummary);
        return true;
      } catch (err) {
        console.warn("BuildingCertificationSummary API error: ", err);
        return false;
      }
    },
    async getBuildingCerts( { commit }, bin) {
      // console.log("getBuildingCerts, bin:", bin)
      try {
        let buildingCerts;
        if (API_SOURCES.buildingCerts === 'arcgis') {
          let binValues = bin.split('|');
          let whereClause = binValues.map(b => `BIN='${b}'`).join(' OR ');
          const url = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/BUILDING_CERTS/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&f=json`;
          const response = await fetch(url);
          if (response.ok) {
            const jsonData = await response.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              attrs.inspectiondate = epochToIso(attrs.inspectiondate);
              attrs.expirationdate = epochToIso(attrs.expirationdate);
              return attrs;
            }) : [];
            buildingCerts = { data: { rows: mappedRows } };
          } else {
            buildingCerts = { data: { rows: [] } };
          }
        } else {
          buildingCerts = await fetchSqlRows('buildingCerts', buildingCertsQuery(bin));
        }
        // console.log("setting buildingCerts:", buildingCerts);
        commit('setBuildingCerts', buildingCerts);
        return true;
      } catch (err) {
        console.warn("BuildingCerts API error: ", err);
        return false;
      }
    },
    async getBuildingFootprints( { commit }, bin) {
      let binSplit = bin.split('|');
      let whereClause = '';
      for (let [index, bin] of binSplit.entries()) {
        whereClause += "'" + bin + "'";
        // whereClause += '%27' + bin + '%27';
        if (index < binSplit.length - 1) {
          whereClause += ',';
        }
      }
      // console.log('getBuildingFootprints, binSplit:', binSplit, 'bin:', bin, 'whereClause:', whereClause);
      try {
        if (API_SOURCES.liBuildingFootprints === 'databridge') {
          // building_footprints_3857 carries the NAD83->WGS84 shift and aligns with
          // the Esri basemap, matching what the AGO layer serves
          const result = await fetchDatabridgeGeoJSON(`select ${FOOTPRINTS_DATABRIDGE_COLS}, ST_AsGeoJSON(ST_Transform(shape, 4326)) as geom from building_footprints_3857 where bin IN (${whereClause})`);
          if (result) {
            // reshape to the ArcGIS pjson shape setBuildingFootprints reads:
            // features carrying attributes + geometry.rings
            commit('setBuildingFootprints', { data: { features: result.features.map((f) => ({
              attributes: f.properties,
              geometry: { rings: f.geometry ? (f.geometry.type === 'MultiPolygon' ? f.geometry.coordinates.flat() : f.geometry.coordinates) : [] },
            })) } });
            return true;
          }
          console.warn('buildingFootprints - databridge request failed, falling back to direct arcgis');
        }
        let esriParams = {
          where: 'bin IN (' + whereClause + ')',
          outFields: '*',
          outSR: 4326,
          f: 'pjson',
        }
        let buildingFootprints = await esriAxios.get('/', { params: esriParams });
        commit('setBuildingFootprints', buildingFootprints);
        return true;
      } catch (err) {
        console.warn("BuildingFootprints API error: ", err);
        return false;
      }
    },
    
    async getAppealsCourtHistory( { commit }, caseNumber, addressId) {
      try {
        let courtHistory = await fetchSqlRows('appealsCourtHistory', appealsCourtHistoryQuery(caseNumber, addressId));
        commit('setAppealsCourtHistory', courtHistory) ;
        return true;
      } catch (err) {
        console.warn("Court History API error: ", err)
        return false;
      }
    },
    async getZoningBoardHistory( { commit }, caseNumber, addressId) {
      try {
        let zbaHistory = await fetchSqlRows('zoningBoardHistory', appealsZoningBoardQuery(caseNumber, addressId));
        commit('setZoningBoardHistory', zbaHistory ) ;
        return true;
      } catch (err) {
        console.warn("Zoning Board API error: ", err)
        return false;
      }
    },
    async getDataByAddress( { commit, dispatch }, payload ) {
      try {
        // console.log("this.aisAxios: ", aisAxios);
        let response = await aisAxios.get(`/${encodeURIComponent(payload)}`);
        // console.log("Axios Results: ", payload, { ...response.data.features[0].properties });
        commit('setAddressData', { ...response.data.features[0].properties });
        if(response.data) {
          const pwd_parcel_id = response.data.features[0].properties.pwd_parcel_id;
          const opa_account_num = response.data.features[0].properties.opa_account_num;
          const addressId = response.data.features[0].properties.li_address_key;
          const eclipse_location_id = response.data.features[0].properties.eclipse_location_id.replace(/\|/g, "', '");
          const streetaddress = response.data.features[0].properties.street_address;
          const bin = response.data.features[0].properties.bin;
          await dispatch('getMailingAddress', pwd_parcel_id);
          // console.log(addressId, pwd_parcel_id, response.data.features[0])
          await dispatch('getBuildingCerts', bin);
          await dispatch('getBuildingFootprints', bin);
          await dispatch('getBuildingCertificationSummary', bin);
          await dispatch('getPropertyHistorySources', [opa_account_num, streetaddress, addressId, eclipse_location_id, pwd_parcel_id ] );
          commit('propHistStatus');
        }
      } catch (err) {
        throw new Error(err)
      }
    },
    async getAWSFileSize({commit}, payload ){
      // console.log("getAWSFileSize: ", payload)
      let string = payload.a;
      let eclipse = payload.eclipse;
      commit
      let size = await sizeOf(string, eclipse)
      return size;
    },

    async getPropertyHistorySources({ commit }, [opa_account_num , streetaddress, addressId, eclipse_location_id, pwd_parcel_id]){
      // console.log(addressId, pwd_parcel_id)
      try {
        // Fetch violations - ArcGIS or Carto based on config
        let violationsData;
        if (API_SOURCES.violations === 'arcgis') {
          let whereParts = [];
          if (opa_account_num) whereParts.push(`OPA_ACCOUNT_NUM='${opa_account_num}'`);
          if (streetaddress) whereParts.push(`ADDRESS='${streetaddress}'`);
          if (addressId) whereParts.push(`ADDRESSOBJECTID='${addressId}'`);
          if (pwd_parcel_id) whereParts.push(`PARCEL_ID_NUM='${pwd_parcel_id}'`);
          const whereClause = whereParts.length > 0 ? whereParts.join(' OR ') : '1=1';
          // Use VIOLATIONS service (not LI_VIOLATIONS) - matches Carto field names
          const violationsUrl = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/VIOLATIONS/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&orderByFields=CASENUMBER+DESC&f=json`;
          const violationsResponse = await fetch(violationsUrl);
          if (violationsResponse.ok) {
            const jsonData = await violationsResponse.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              // Convert epoch dates to ISO format
              attrs.casecreateddate = epochToIso(attrs.casecreateddate);
              attrs.mostrecentinvestigation = epochToIso(attrs.mostrecentinvestigation);
              attrs.casecompleteddate = epochToIso(attrs.casecompleteddate);
              return attrs;
            }) : [];
            violationsData = { data: { rows: mappedRows } };
          } else {
            violationsData = { data: { rows: [] } };
          }
        } else {
          violationsData = await fetchSqlRows('violations', violationQuery(opa_account_num ,streetaddress, addressId, eclipse_location_id, pwd_parcel_id));
        }

        // Fetch permits - ArcGIS or Carto based on config
        let permitsData;
        if (API_SOURCES.permits === 'arcgis') {
          let whereParts = [];
          if (opa_account_num) whereParts.push(`OPA_ACCOUNT_NUM='${opa_account_num}'`);
          if (streetaddress) whereParts.push(`ADDRESS='${streetaddress}'`);
          if (addressId) whereParts.push(`ADDRESSOBJECTID='${addressId}'`);
          if (pwd_parcel_id) whereParts.push(`PARCEL_ID_NUM='${pwd_parcel_id}'`);
          const whereClause = whereParts.length > 0 ? whereParts.join(' OR ') : '1=1';
          const permitsUrl = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/PERMITS/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&orderByFields=PERMITTYPE&f=json`;
          const permitsResponse = await fetch(permitsUrl);
          if (permitsResponse.ok) {
            const jsonData = await permitsResponse.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              attrs.permitissuedate = epochToIso(attrs.permitissuedate);
              return attrs;
            }) : [];
            permitsData = { data: { rows: mappedRows } };
          } else {
            permitsData = { data: { rows: [] } };
          }
        } else {
          permitsData = await fetchSqlRows('permits', permitsQuery(opa_account_num ,streetaddress, addressId, eclipse_location_id, pwd_parcel_id));
        }

        // Fetch business licenses - ArcGIS or Carto based on config
        let licensesData;
        if (API_SOURCES.businessLicenses === 'arcgis') {
          let whereParts = [];
          if (opa_account_num) whereParts.push(`OPA_ACCOUNT_NUM='${opa_account_num}'`);
          if (streetaddress) whereParts.push(`ADDRESS='${streetaddress}'`);
          if (addressId) whereParts.push(`ADDRESSOBJECTID='${addressId}'`);
          if (pwd_parcel_id) whereParts.push(`PARCEL_ID_NUM='${pwd_parcel_id}'`);
          const addressedLicenseFilter = ` AND ADDRESSED_LICENSE='Yes'`;
          const whereClause = whereParts.length > 0 ? `(${whereParts.join(' OR ')})${addressedLicenseFilter}` : `1=1${addressedLicenseFilter}`;
          const licensesUrl = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/BUSINESS_LICENSES/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&orderByFields=LICENSETYPE&f=json`;
          const licensesResponse = await fetch(licensesUrl);
          if (licensesResponse.ok) {
            const jsonData = await licensesResponse.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              attrs.initialissuedate = epochToIso(attrs.initialissuedate);
              attrs.inactivedate = epochToIso(attrs.inactivedate);
              return attrs;
            }) : [];
            licensesData = { data: { rows: mappedRows } };
          } else {
            licensesData = { data: { rows: [] } };
          }
        } else {
          licensesData = await fetchSqlRows('businessLicenses', licensesQuery(opa_account_num ,streetaddress, addressId, eclipse_location_id, pwd_parcel_id));
        }

        // Fetch appeals - ArcGIS or Carto based on config
        let appealsData;
        if (API_SOURCES.appeals === 'arcgis') {
          let whereParts = [];
          if (opa_account_num) whereParts.push(`OPA_ACCOUNT_NUM='${opa_account_num}'`);
          if (streetaddress) whereParts.push(`ADDRESS='${streetaddress}'`);
          if (addressId) whereParts.push(`ADDRESSOBJECTID='${addressId}'`);
          if (pwd_parcel_id) whereParts.push(`PARCEL_ID_NUM='${pwd_parcel_id}'`);
          const whereClause = whereParts.length > 0 ? whereParts.join(' OR ') : '1=1';
          const appealsUrl = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/APPEALS/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&orderByFields=SCHEDULEDDATE+DESC&f=json`;
          const appealsResponse = await fetch(appealsUrl);
          if (appealsResponse.ok) {
            const jsonData = await appealsResponse.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              attrs.scheduleddate = epochToIso(attrs.scheduleddate);
              return attrs;
            }) : [];
            appealsData = { data: { rows: mappedRows } };
          } else {
            appealsData = { data: { rows: [] } };
          }
        } else {
          appealsData = await fetchSqlRows('appeals', appealsQuery(opa_account_num ,streetaddress, addressId, eclipse_location_id, pwd_parcel_id));
        }

        // Fetch investigations - ArcGIS or Carto based on config
        let investigationData;
        if (API_SOURCES.investigations === 'arcgis') {
          // Note: CASE_INVESTIGATIONS service uses lowercase field names (unlike other services)
          // addressId and eclipse_location_id may contain multiple pipe-separated values
          let whereParts = [];
          if (opa_account_num) whereParts.push(`opa_account_num='${opa_account_num}'`);
          if (streetaddress) whereParts.push(`address='${streetaddress}'`);
          if (addressId) {
            const addressIdFormatted = addressId.replace(/\|/g, "','");
            whereParts.push(`addressobjectid IN ('${addressIdFormatted}')`);
          }
          if (pwd_parcel_id) whereParts.push(`parcel_id_num='${pwd_parcel_id}'`);
          if (eclipse_location_id) {
            const eclipseIdFormatted = eclipse_location_id.replace(/\|/g, "','");
            whereParts.push(`addressobjectid IN ('${eclipseIdFormatted}')`);
          }
          const whereClause = whereParts.length > 0 ? whereParts.join(' OR ') : '1=1';
          const investigationsUrl = `https://services.arcgis.com/fLeGjb7u4uXqeF9q/ArcGIS/rest/services/CASE_INVESTIGATIONS/FeatureServer/0/query?where=${encodeURIComponent(whereClause)}&outFields=*&f=json`;
          const investigationsResponse = await fetch(investigationsUrl);
          if (investigationsResponse.ok) {
            const jsonData = await investigationsResponse.json();
            const mappedRows = jsonData.features ? jsonData.features.map(f => {
              const attrs = {};
              for (const key in f.attributes) {
                attrs[key.toLowerCase()] = f.attributes[key];
              }
              attrs.investigationcompleted = epochToIso(attrs.investigationcompleted);
              return attrs;
            }) : [];
            investigationData = { data: { rows: mappedRows } };
          } else {
            investigationData = { data: { rows: [] } };
          }
        } else {
          investigationData = await fetchSqlRows('investigations', liInvestigationQuery(opa_account_num ,streetaddress, addressId, eclipse_location_id, pwd_parcel_id));
        }

        await Promise.all([violationsData, permitsData, licensesData, appealsData]).then(response => {
          try {
            // console.log("Create mutations for all 4 returns", response)
            commit("setViolationsData", violationsData);
            commit("setPermitsData", permitsData);
            commit("setLicensesData", licensesData);
            commit("setAppealsData", appealsData);
            commit("setInvestigationData", investigationData);
            return response
          } catch (err) {
            console.warn("There is an error with a source", err);
          }
        });
        return true;
        } catch (err) {
          return false;
        }
    },
    async getPermitAIS({ commit, dispatch }, payload) {
      dispatch;
      // let query = permitsQuery(params);
      // console.log(payload);
      let acctNum = payload.opa_account_num

      payload.permitContractorAddress = []
      payload.contractoraddress1 ? payload.permitContractorAddress.push(payload.contractoraddress1) : payload.permitContractorAddress.push("");
      payload.contractoraddress2 !== null ? payload.permitContractorAddress.push(payload.contractoraddress2) : "" ;
      payload.permitContractorAddress.push([ payload.contractorcity, payload.contractorstate, payload.contractorzip ].join(" "));

      payload.permitAddress = [];
      payload.permitAddress.push(payload.address);
      payload.unit_num !== null ? payload.permitAddress.push([payload.unit_type !== null ? payload.unit_type : "Unit", payload.unit_num].join(" ")): "";
      payload.permitAddress.push("Philadelphia, PA " + payload.zip);

      try {
        let response = await aisAxios.get(`/${encodeURIComponent(acctNum)}`);
        // console.log("getPermitAIS: ", response);
          let detailInfo = { permit: payload, ais: response.data.features[0].properties };
          commit('openDetail', detailInfo );
          // console.log("getPermitsDetails: ", response.data.features[0]);
      } catch (err) {
          throw new Error(err)
      } 
    },
    async getMoreLicenseDetailInfo({ commit, dispatch }, payload) {
      dispatch;
      console.log("getMoreLicenseDetailInfo: ", payload);
      let license = [];
      license.id = payload;
      let query = {
        table: 'trade_licenses',
        fields: 'commercialactivitylicense, contactname, companyname, icccategory, licensenumber, licensetype, licensestatus, issuedate, expirationdate, objectid',
        where: `licensenumber = '${payload}'`,
      };
      try {
        const licenseInfo = await fetchSqlRows('contractorLicenses', query);
        console.log('licenseInfo:', licenseInfo);
        commit('openDetail', licenseInfo.data.rows[0]);
      } catch (err) {
        console.warn("getMoreLicenseDetailInfo API error: ", err)
      }
    }
  }
};
// });

export default new Vuex.Store(localStore);

